import { apiClient } from '../lib/apiClient';
import { environmentService, LogLevel } from './environmentService';

export interface LogEntry {
  level: LogLevel;
  message: string;
  category: string;
  orgId?: string;
  userId?: string;
  sessionId?: string;
  correlationId?: string;
  metadata?: Record<string, unknown>;
  error?: {
    message: string;
    stack?: string;
    code?: string;
  };
  performance?: {
    duration?: number;
    operationType?: string;
  };
  environment: string;
  timestamp: string;
}

class LoggingService {
  private sessionId: string;
  private correlationId: string | null = null;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    const stored = sessionStorage.getItem('session_id');
    if (stored) return stored;

    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('session_id', newId);
    return newId;
  }

  setCorrelationId(id: string) {
    this.correlationId = id;
  }

  clearCorrelationId() {
    this.correlationId = null;
  }

  private async writeLog(entry: LogEntry) {
    if (!environmentService.shouldLog(entry.level)) {
      return;
    }

    if (entry.level === 'debug' || entry.level === 'info') {
      console.log(`[${entry.level.toUpperCase()}] ${entry.category}: ${entry.message}`, entry.metadata);
    } else if (entry.level === 'warning') {
      console.warn(`[${entry.level.toUpperCase()}] ${entry.category}: ${entry.message}`, entry.metadata);
    } else {
      console.error(`[${entry.level.toUpperCase()}] ${entry.category}: ${entry.message}`, entry.metadata);
    }

    try {
      await apiClient.post('/application-logs', {
        level: entry.level,
        message: entry.message,
        category: entry.category,
        org_id: entry.orgId || null,
        user_id: entry.userId || null,
        session_id: entry.sessionId || this.sessionId,
        correlation_id: entry.correlationId || this.correlationId,
        metadata: entry.metadata || {},
        error_details: entry.error || null,
        performance_metrics: entry.performance || null,
        environment: entry.environment,
        created_at: entry.timestamp,
      });
    } catch (err) {
      console.error('Failed to write application log:', err);
    }
  }

  debug(category: string, message: string, metadata?: Record<string, unknown>, userId?: string, orgId?: string) {
    this.writeLog({
      level: 'debug',
      message,
      category,
      userId,
      orgId,
      metadata,
      environment: environmentService.getEnvironment(),
      timestamp: new Date().toISOString(),
    });
  }

  info(category: string, message: string, metadata?: Record<string, unknown>, userId?: string, orgId?: string) {
    this.writeLog({
      level: 'info',
      message,
      category,
      userId,
      orgId,
      metadata,
      environment: environmentService.getEnvironment(),
      timestamp: new Date().toISOString(),
    });
  }

  warning(category: string, message: string, metadata?: Record<string, unknown>, userId?: string, orgId?: string) {
    this.writeLog({
      level: 'warning',
      message,
      category,
      userId,
      orgId,
      metadata,
      environment: environmentService.getEnvironment(),
      timestamp: new Date().toISOString(),
    });
  }

  error(category: string, message: string, error?: Error, metadata?: Record<string, unknown>, userId?: string, orgId?: string) {
    this.writeLog({
      level: 'error',
      message,
      category,
      userId,
      orgId,
      metadata,
      error: error ? {
        message: error.message,
        stack: error.stack,
        code: (error as any).code,
      } : undefined,
      environment: environmentService.getEnvironment(),
      timestamp: new Date().toISOString(),
    });
  }

  critical(category: string, message: string, error?: Error, metadata?: Record<string, unknown>, userId?: string, orgId?: string) {
    this.writeLog({
      level: 'critical',
      message,
      category,
      userId,
      orgId,
      metadata,
      error: error ? {
        message: error.message,
        stack: error.stack,
        code: (error as any).code,
      } : undefined,
      environment: environmentService.getEnvironment(),
      timestamp: new Date().toISOString(),
    });
  }

  logPerformance(
    category: string,
    operationType: string,
    duration: number,
    metadata?: Record<string, unknown>,
    userId?: string,
    orgId?: string
  ) {
    const level: LogLevel = duration > 3000 ? 'warning' : 'info';

    this.writeLog({
      level,
      message: `${operationType} completed in ${duration}ms`,
      category,
      userId,
      orgId,
      metadata,
      performance: {
        duration,
        operationType,
      },
      environment: environmentService.getEnvironment(),
      timestamp: new Date().toISOString(),
    });
  }

  async logUserAction(
    action: string,
    category: string,
    userId: string,
    orgId: string,
    metadata?: Record<string, unknown>
  ) {
    this.info(category, `User action: ${action}`, {
      action,
      ...metadata,
    }, userId, orgId);
  }

  async logApiCall(
    method: string,
    endpoint: string,
    duration: number,
    statusCode: number,
    userId?: string,
    orgId?: string,
    error?: Error
  ) {
    const level: LogLevel = statusCode >= 500 ? 'error' : statusCode >= 400 ? 'warning' : 'info';

    this.writeLog({
      level,
      message: `API ${method} ${endpoint} - ${statusCode} (${duration}ms)`,
      category: 'api',
      userId,
      orgId,
      metadata: {
        method,
        endpoint,
        statusCode,
      },
      performance: {
        duration,
        operationType: 'api_call',
      },
      error: error ? {
        message: error.message,
        stack: error.stack,
      } : undefined,
      environment: environmentService.getEnvironment(),
      timestamp: new Date().toISOString(),
    });
  }

  getSessionId(): string {
    return this.sessionId;
  }
}

export const loggingService = new LoggingService();
